import { useMemo, useState } from 'react';
import Fuse, { type FuseOptionKey } from 'fuse.js';
import { _isBlank } from '@/littledash';

type UseFuseProps<T> = {
  entries: ReadonlyArray<T>;
  /**
   * Must be a stable reference — hoist to a module-level constant rather than passing an inline literal,
   * otherwise the search index is rebuilt on every render of the consuming component.
   */
  keys?: Array<FuseOptionKey<T>>;
};

export const useFuse = <T>({ entries, keys }: UseFuseProps<T>) => {
  const [filter, setFilter] = useState('');
  const fuse = useMemo(() => new Fuse(entries, { includeScore: false, ignoreLocation: true, keys }), [entries, keys]);

  const results = useMemo<UseFuseProps<T>['entries']>(() => {
    if (_isBlank(filter)) {
      return entries;
    }
    return fuse.search(filter.trim()).map(({ item }) => item);
  }, [fuse, entries, filter]);

  return {
    results,
    filter,
    updateFilter(filterUpdate: string) {
      setFilter(() => (typeof filterUpdate === 'string' ? filterUpdate.trimStart() : ''));
    },
  };
};
